import React, { useEffect, useMemo, useState } from 'react';
import { availableFormats } from '../utils/memeFormats';
import { buildPostText, randomCaption, sanitizeMemeText } from '../utils/memeCaptions';
import { renderMemeImage } from '../utils/memeRender';
import type { MemeContext, MemeFormatId, MemeSizeId } from '../utils/memeTypes';
import { MEME_SIZES } from '../utils/memeTypes';
import './PropagandaModal.css';

interface Props {
  context: MemeContext;
  onClose: () => void;
}

const MemeModal: React.FC<Props> = ({ context, onClose }) => {
  const formats = useMemo(() => availableFormats(context), [context]);
  const [formatId, setFormatId] = useState<MemeFormatId>(
    () => formats[0]?.id || 'dispatch'
  );
  const [sizeId, setSizeId] = useState<MemeSizeId>('landscape');
  const [caption, setCaption] = useState('');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!formats.some((f) => f.id === formatId) && formats[0]) {
      setFormatId(formats[0].id);
    }
  }, [formats, formatId]);

  useEffect(() => {
    setCaption(randomCaption(formatId, context));
    setBlob(null);
  }, [formatId, context]);

  useEffect(() => {
    if (!blob) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const shuffle = () => {
    setCaption(randomCaption(formatId, context));
    setBlob(null);
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const out = await renderMemeImage(
        formatId,
        context,
        sanitizeMemeText(caption),
        sizeId
      );
      setBlob(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to render meme');
    } finally {
      setGenerating(false);
    }
  };

  const flash = (msg: string) => {
    setNote(msg);
    window.setTimeout(() => setNote(null), 2200);
  };

  const postText = buildPostText(sanitizeMemeText(caption), context.mapUrl);
  const fileName = `empire-maps-meme-${formatId}-${Date.now()}.png`;

  const download = () => {
    if (!blob || !previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    flash('Downloaded');
  };

  const copyImage = async () => {
    if (!blob) return;
    try {
      const ClipboardItemCtor = (window as unknown as { ClipboardItem: typeof ClipboardItem })
        .ClipboardItem;
      await navigator.clipboard.write([
        new ClipboardItemCtor({ [blob.type || 'image/png']: blob }),
      ]);
      flash('Image copied');
    } catch {
      flash('Could not copy image');
    }
  };

  const nativeShare = async () => {
    if (!blob) return;
    const file = new File([blob], fileName, { type: blob.type || 'image/png' });
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Empire Maps',
          text: postText,
        });
        return;
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
    flash('Share unavailable — try Download or Post on X');
  };

  const postOnX = () => {
    const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(postText)}`;
    window.open(intent, '_blank', 'noopener,noreferrer');
  };

  const canShareFiles =
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    blob != null &&
    navigator.canShare({
      files: [new File([blob], fileName, { type: 'image/png' })],
    });

  const canCopyImage =
    typeof window !== 'undefined' &&
    typeof ClipboardItem !== 'undefined' &&
    !!navigator.clipboard?.write;

  return (
    <div className="empire-modal-overlay" onClick={onClose}>
      <div
        className="empire-modal empire-modal--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="empire-modal-head">
          <h2>Battle meme</h2>
          <button
            type="button"
            className="empire-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <p className="prop-lead">
          Pick a format, shuffle the copy, export at feed size. Cartoon war paperwork only.
        </p>

        {formats.length === 0 ? (
          <p className="prop-error">
            Select a team or conquered county on the map, then open Meme again.
          </p>
        ) : (
          <>
            <div className="prop-templates">
              {formats.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`prop-template${formatId === f.id ? ' active' : ''}`}
                  onClick={() => setFormatId(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="prop-templates" role="group" aria-label="Output size">
              {(Object.keys(MEME_SIZES) as MemeSizeId[]).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`prop-template${sizeId === id ? ' active' : ''}`}
                  onClick={() => {
                    setSizeId(id);
                    setBlob(null);
                  }}
                >
                  {MEME_SIZES[id].label}
                </button>
              ))}
            </div>

            <label className="prop-field">
              Caption
              <input
                value={caption}
                onChange={(e) => {
                  setCaption(e.target.value);
                  setBlob(null);
                }}
                maxLength={180}
              />
            </label>

            <div className="prop-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="empire-btn" onClick={shuffle}>
                🎲 Shuffle
              </button>
              <button
                type="button"
                className="empire-btn empire-btn--primary"
                onClick={generate}
                disabled={generating}
              >
                {generating ? 'Rendering…' : blob ? 'Re-render' : 'Generate meme'}
              </button>
            </div>
          </>
        )}

        {error && <p className="prop-error">{error}</p>}

        {previewUrl && (
          <div className="snap-preview" style={{ marginTop: '1rem' }}>
            <img src={previewUrl} alt="Meme preview" />
          </div>
        )}

        {blob && (
          <div className="snap-actions" style={{ marginTop: '0.75rem' }}>
            <button type="button" className="empire-btn empire-btn--solid" onClick={download}>
              ⬇ Download
            </button>
            {canCopyImage && (
              <button type="button" className="empire-btn" onClick={copyImage}>
                ⧉ Copy image
              </button>
            )}
            {canShareFiles && (
              <button type="button" className="empire-btn" onClick={nativeShare}>
                ↗ Share
              </button>
            )}
            <button type="button" className="empire-btn" onClick={postOnX}>
              Post on X
            </button>
          </div>
        )}

        {note && <div className="snap-note">{note}</div>}
      </div>
    </div>
  );
};

export default MemeModal;
